import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router';
import { object, string } from 'yup';
import useForm from '@jwp/ott-hooks-react/src/useForm';
import { PATH_SIGN_IN, PATH_RESET_PASSWORD } from '@jwp/ott-common/src/paths';
import { useAuth } from '@jwp/ott-hooks-react/src/useAuth';
import { useOtpInput } from '@jwp/ott-hooks-react/src/useOTPInput';
import { modalURLFromLocation } from '@jwp/ott-ui-react/src/utils/location';
import { useConfigStore } from '@jwp/ott-common/src/stores/ConfigStore';
import { showToast } from '@jwp/ott-common/src/stores/ToastStore';

import LoadingOverlay from '../LoadingOverlay/LoadingOverlay';
import Link from '../Link/Link';
import styles from '../SignIn/SignIn.module.scss';

import { EmailVerificationForm } from './EmailVerificationForm';
import { OTPVerificationForm } from './OTPVerificationForm';

function BrandLogo({ appConfig, siteName }: Readonly<{ appConfig: any; siteName: string | undefined }>) {
  return (
    <div className={styles.brand}>
      {appConfig?.branding?.logo_url ? (
        <img src={appConfig.branding.logo_url} alt={siteName} className={styles.brandLogoImage} />
      ) : (
        <div className={styles.brandLogo} />
      )}
    </div>
  );
}

function SignInBackgroundVideo() {
  return (
    <div className={styles.videoLayer} aria-hidden>
      <video className={styles.videoBackground} autoPlay muted loop playsInline preload="metadata">
        <source src="/videos/signin-background.mp4" type="video/mp4" />
      </video>
    </div>
  );
}

const ForgotPassword = () => {
  const { siteName, appConfig } = useConfigStore((s) => ({ siteName: s.config.siteName, appConfig: s.appConfig }));
  const { t } = useTranslation('account');
  const navigate = useNavigate();
  const location = useLocation();

  const {
    requestOTP,
    requestOTPLoading,
    requestOTPData,
    OTPVerify,
    otpVerifyError,
    otpVerifyLoading,
    recoveryLoading,
    recoveryMyUsername,
  } = useAuth();

  const {
    otp,
    inputRefs,
    handleInputChange,
    handleKeyDown,
    handlePaste,
    handleFocus,
    otpValue,
    clear,
  } = useOtpInput(4);

  const [isSentOTP, setIsSentOTP] = useState(false);
  const [otpError, setOtpError] = useState(false);
  const [timer, setTimer] = useState(0);
  const [expiryDate, setExpiryDate] = useState<Date | null>(null);
  const [emailError, setEmailError] = useState("");

  const { values, errors, submitting, handleSubmit, handleChange } = useForm<{ email: string }>({
    initialValues: { email: '' },
    silentValidation: true,
    validationSchema: object().shape({
      email: string().email(t('login.field_is_not_valid_email')).required(t('login.field_required', { field: t('login.email') })),
    }),
    onSubmit: async ({ email }) => {
      clear();
      try {
        const response: any = await requestOTP(email);
        
        if (response?.errors && response.errors.length > 0) {
          throw new Error(response.errors[0].message);
        }

        if (response?.data) {
          const type = response?.data?.meetRequestForgetPasswordOtp?.type;
          if (type === "otp") {
            setIsSentOTP(true);
            setExpiryDate(new Date(response?.data?.meetRequestForgetPasswordOtp?.expiresAt));
          } else {
            const message = response?.data?.meetRequestForgetPasswordOtp?.message || t('reset.reset_link_sent');
            navigate(modalURLFromLocation(location, 'reset-password-success-play', { message }));
          }
        }
      } catch (err: any) {
        console.error(err);
        throw err;
      }
    },
    onSubmitError: ({ error }) => {
      let errorMessage = t('login.unexpected_error');
      if (error instanceof Error) {
        errorMessage = error.message;
      } else if (typeof error === 'string') {
        errorMessage = error;
      }
      showToast(errorMessage, 'error');
    },
  });

  const handleRecoveryByUsername = async () => {
    const email = values.email;
    if (!email) return;
    
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setEmailError(t('login.field_is_not_valid_email'));
      return;
    }

    try {
      const response = await recoveryMyUsername(email);

      if (response?.error) {
        showToast(response.error.message, 'error');
        return;
      }

      if (response?.data) {
        navigate(modalURLFromLocation(location, 'recovery-username'));
      }
    } catch (err: any) {
      console.error(err);
      showToast(err.message || t('login.unexpected_error'), 'error');
    } finally {
      setEmailError("");
    }
  };

  const handleOTPVerification = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (otpValue.length < 4) {
      setOtpError(true);
      return;
    }
    try {
      setOtpError(false);
      const emailId = (requestOTPData as any)?.meetRequestForgetPasswordOtp?.emailId || values.email;
      const response = await OTPVerify(otpValue, emailId);
      if (response?.data) {
        sessionStorage.setItem("fp_email", emailId);
        sessionStorage.setItem("fp_code", otpValue);
        navigate(PATH_RESET_PASSWORD);
      }
    } catch (err: any) {
      console.error(err);
      showToast(err.message || t('reset.invalid_otp'), 'error');
    }
  };

  const [resendError, setResendError] = useState<string | null>(null);

  const handleResendAction = async () => {
    setResendError(null);
    try {
      const emailId = (requestOTPData as any)?.meetRequestForgetPasswordOtp?.emailId || values.email;
      const response: any = await requestOTP(emailId);
      
      if (response?.errors && response.errors.length > 0) {
        setResendError(response.errors[0].message);
        return;
      }

      if (response?.data) {
        const type = response?.data?.meetRequestForgetPasswordOtp?.type;
        if (type === 'otp') {
          setExpiryDate(new Date(response?.data?.meetRequestForgetPasswordOtp?.expiresAt));
          showToast(t('reset.otp_sent_to', { email: emailId }), 'success');
        } else {
          const message = response?.data?.meetRequestForgetPasswordOtp?.message || t('reset.reset_link_sent');
          navigate(modalURLFromLocation(location, 'reset-password-success-play', { message }));
        }
      }
    } catch (err: any) {
      console.error(err);
      showToast(err.message || "Failed to resend code", 'error');
      setResendError(err.message || "Failed to resend code");
    }
  };

  useEffect(() => {
    if (isSentOTP && expiryDate) {
      const calculateSecondsLeft = () => {
        const now = Date.now();
        const expiryTimestamp = expiryDate.getTime();
        const difference = expiryTimestamp - now;
        const remainingSeconds = Math.max(0, Math.floor(difference / 1000));
        setTimer(remainingSeconds);
        return remainingSeconds;
      };

      calculateSecondsLeft();
      const interval = setInterval(() => {
        if (calculateSecondsLeft() === 0) {
          clearInterval(interval);
        }
      }, 1000);

      return () => clearInterval(interval);
    }
  }, [isSentOTP, expiryDate]);

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs < 10 ? '0' : ''}${secs}`;
  };

  const currentEmail = (requestOTPData as any)?.meetRequestForgetPasswordOtp?.emailId || values.email;

  return (
    <div className={styles.root}>
      <Helmet>
        <title>{t('reset.forgot_password')}</title>
      </Helmet>
      <SignInBackgroundVideo />
      <BrandLogo appConfig={appConfig} siteName={siteName} />
      <main className={styles.main}>
        <div className={styles.overlayWrap}>
          {submitting || otpVerifyLoading || requestOTPLoading ? (
            <div className={styles.loaderLayer} aria-live="polite" aria-busy>
              <LoadingOverlay transparentBackground inline />
            </div>
          ) : null}
          <div className={styles.card}>
            {isSentOTP ? (
              <OTPVerificationForm
                email={currentEmail}
                otp={otp}
                inputRefs={inputRefs}
                otpValue={otpValue}
                otpError={otpError}
                otpVerifyLoading={otpVerifyLoading}
                otpVerifyError={otpVerifyError as any}
                timer={timer}
                formatTime={formatTime}
                handleInputChange={handleInputChange}
                handleKeyDown={handleKeyDown}
                handlePaste={handlePaste}
                handleFocus={handleFocus}
                handleOTPVerification={handleOTPVerification}
                handleResendOTP={handleResendAction}
                resendError={resendError}
              />
            ) : (
              <EmailVerificationForm
                values={values}
                errors={errors}
                emailError={emailError}
                submitting={submitting}
                handleChange={handleChange}
                handleSubmit={handleSubmit}
                handleRecoveryByUsername={handleRecoveryByUsername}
                recoveryLoading={recoveryLoading}
              />
            )}
            <p className={styles.footer}>
              {t('reset.remember_password')}{' '}
              <Link className={styles.signUpLink} to={PATH_SIGN_IN}>
                {t('reset.sign_in')}
              </Link>
            </p>
          </div>
        </div>
      </main>
    </div>
  );
};

export default ForgotPassword;
